import { html, type TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-fader.css';

const FADER_SIZE = { SM: 'sm', MD: 'md', LG: 'lg' } as const;
type FaderSize = (typeof FADER_SIZE)[keyof typeof FADER_SIZE];

interface FaderEventDetail {
  value: number;
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-fader': ExtFader;
  }
}

/**
 * <ext-fader value="50" min="0" max="100" label="Volume" unit="%" name="volume" />
 *
 * Control deslizante vertical genérico. Light DOM.
 *
 * Interacción:
 *   - Drag del thumb (pointer capture) → actualiza value continuamente → emite ext:input
 *   - Soltar → emite ext:change si el valor cambió
 *   - Click en el track → salta al valor relativo → emite ext:change
 *   - Teclado (ArrowUp/Down, PageUp/Down, Home, End) → emite ext:input + ext:change
 *
 * Props: value, min, max, step, label, unit, name, size, disabled
 *
 * Eventos:
 *  - ext:input  — { value } en cada cambio interactivo (drag, key)
 *  - ext:change — { value } al soltar drag, click en track o keydown
 */
@customElement('ext-fader')
export class ExtFader extends ExtBaseElement {
  @property({ type: Number }) value: number = 50;
  @property({ type: Number }) min: number = 0;
  @property({ type: Number }) max: number = 100;
  @property({ type: Number }) step: number = 1;
  @property({ type: String }) label: string = '';
  @property({ type: String }) unit: string = '';
  @property({ type: String }) name: string = '';
  @property({ type: String, reflect: true }) size: FaderSize = FADER_SIZE.MD;
  @property({ type: Boolean, reflect: true }) disabled: boolean = false;

  @query('.ext-fader__thumb') private _thumbEl?: HTMLElement;

  private _lastEmittedValue = 50;
  private _isDragging = false;
  private _pointerStartY = 0;
  private _valueAtDragStart = 50;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._lastEmittedValue = this.value;
    this._valueAtDragStart = this.value;
  }

  // ---------------------------------------------------------------------------
  // Math helpers
  // ---------------------------------------------------------------------------

  private _clampAndQuantize(v: number): number {
    const clamped = Math.min(this.max, Math.max(this.min, v));
    // Quantize to step
    const steps = Math.round((clamped - this.min) / this.step);
    return this.min + steps * this.step;
  }

  /** Valor ALTO = thumb ARRIBA. Retorna 0..100 */
  private _percentFromValue(v: number): number {
    if (this.max === this.min) return 0;
    return ((v - this.min) / (this.max - this.min)) * 100;
  }

  private _valueFromPercent(pct: number): number {
    return this.min + (pct / 100) * (this.max - this.min);
  }

  // ---------------------------------------------------------------------------
  // Pointer interaction (drag on thumb with pointer capture)
  // ---------------------------------------------------------------------------

  private _onPointerDown = (ev: PointerEvent): void => {
    if (this.disabled) return;
    ev.preventDefault();
    ev.stopPropagation();
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    this._pointerStartY = ev.clientY;
    this._valueAtDragStart = this.value;
    this._isDragging = true;
  };

  private _onPointerMove = (ev: PointerEvent): void => {
    if (!this._isDragging) return;
    const deltaY = ev.clientY - this._pointerStartY;
    const trackEl = this.querySelector('.ext-fader__track');
    const trackHeight = trackEl?.getBoundingClientRect().height ?? 180;
    // Drag hacia ARRIBA (deltaY negativo) = valor SUBE
    const deltaValue = (-deltaY / trackHeight) * (this.max - this.min);
    const newValue = this._clampAndQuantize(this._valueAtDragStart + deltaValue);
    this.value = newValue;
    this._emit<FaderEventDetail>('input', { value: this.value });
  };

  private _onPointerUp = (ev: PointerEvent): void => {
    this._isDragging = false;
    (ev.currentTarget as HTMLElement).releasePointerCapture(ev.pointerId);
    if (this.value !== this._lastEmittedValue) {
      this._lastEmittedValue = this.value;
      this._emit<FaderEventDetail>('change', { value: this.value });
    }
  };

  // ---------------------------------------------------------------------------
  // Track click (jump to position)
  // ---------------------------------------------------------------------------

  private _onTrackClick = (ev: PointerEvent): void => {
    if (this.disabled) return;
    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const relativeY = ev.clientY - rect.top;
    // pct: 1 en la parte superior (valor máximo), 0 en la inferior (valor mínimo)
    const pct = (1 - relativeY / rect.height) * 100;
    const newValue = this._clampAndQuantize(this._valueFromPercent(pct));
    this.value = newValue;
    this._lastEmittedValue = this.value;
    this._emit<FaderEventDetail>('change', { value: this.value });
  };

  // ---------------------------------------------------------------------------
  // Keyboard
  // ---------------------------------------------------------------------------

  private _onKeyDown = (ev: KeyboardEvent): void => {
    if (this.disabled) return;

    let newValue = this.value;

    switch (ev.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        newValue = this._clampAndQuantize(this.value + this.step);
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        newValue = this._clampAndQuantize(this.value - this.step);
        break;
      case 'PageUp':
        newValue = this._clampAndQuantize(this.value + this.step * 10);
        break;
      case 'PageDown':
        newValue = this._clampAndQuantize(this.value - this.step * 10);
        break;
      case 'Home':
        newValue = this.min;
        break;
      case 'End':
        newValue = this.max;
        break;
      default:
        return;
    }

    ev.preventDefault();
    this.value = newValue;
    this._emit<FaderEventDetail>('input', { value: this.value });

    if (this.value !== this._lastEmittedValue) {
      this._lastEmittedValue = this.value;
      this._emit<FaderEventDetail>('change', { value: this.value });
    }
  };

  private _onThumbBlur = (): void => {
    if (this.value !== this._lastEmittedValue) {
      this._lastEmittedValue = this.value;
      this._emit<FaderEventDetail>('change', { value: this.value });
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  render(): TemplateResult {
    const pct = this._percentFromValue(this.value);
    return html`
      <div class="ext-fader__root">
        <div class="ext-fader__track-wrap" @pointerdown=${this._onTrackClick}>
          <div class="ext-fader__track">
            <div class="ext-fader__fill" style="height: ${pct}%"></div>
            <div
              class="ext-fader__thumb"
              role="slider"
              tabindex=${this.disabled ? -1 : 0}
              aria-valuemin=${this.min}
              aria-valuemax=${this.max}
              aria-valuenow=${this.value}
              aria-orientation="vertical"
              aria-label=${this.label || 'Slider'}
              aria-disabled=${this.disabled ? 'true' : 'false'}
              style="bottom: ${pct}%"
              @pointerdown=${this._onPointerDown}
              @pointermove=${this._onPointerMove}
              @pointerup=${this._onPointerUp}
              @keydown=${this._onKeyDown}
              @blur=${this._onThumbBlur}
            ></div>
          </div>
        </div>
        ${this.label ? html`<div class="ext-fader__label">${this.label}</div>` : null}
        <div class="ext-fader__value">${this.value}${this.unit ? html` <span class="ext-fader__unit">${this.unit}</span>` : null}</div>
        ${this.name ? html`<input type="hidden" name=${this.name} .value=${String(this.value)} />` : null}
      </div>
    `;
  }
}
